import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Tabs,
  Tab,
  Paper,
  Typography,
  alpha,
  Badge,
  Chip,
} from "@mui/material";

const SectionTabber = ({
  tabs = [],
  value,
  defaultValue,
  onTabChange,
  tabsProps = {},
  paperProps = {},
  contentWrapperSx = {},
  renderLabel,
}) => {
  const resolvedTabs = useMemo(
    () =>
      tabs.map((tab, index) => ({
        ...tab,
        value: tab.value ?? index,
      })),
    [tabs]
  );

  const fallbackValue = useMemo(
    () => resolvedTabs[0]?.value ?? 0,
    [resolvedTabs]
  );

  const isControlled = value !== undefined && value !== null;
  const [internalValue, setInternalValue] = useState(
    defaultValue ?? fallbackValue
  );

  useEffect(() => {
    if (!isControlled) {
      setInternalValue(defaultValue ?? fallbackValue);
    }
  }, [defaultValue, fallbackValue, isControlled]);

  const activeValue = isControlled ? value : internalValue;
  const activeTab =
    resolvedTabs.find((tab) => tab.value === activeValue) ?? resolvedTabs[0];

  const handleTabChange = (event, newValue) => {
    if (!isControlled) {
      setInternalValue(newValue);
    }
    onTabChange?.(event, newValue);
  };

  const defaultRenderLabel = (tab) => (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {tab.icon && <Box component='span'>{tab.icon}</Box>}
      <Box>
        <Typography variant='body2' fontWeight='inherit'>
          {tab.label}
        </Typography>
        {tab.description && (
          <Typography
            variant='caption'
            sx={{
              color: "text.secondary",
              fontSize: "0.7rem",
              display: "block",
              mt: 0.25,
            }}
          >
            {tab.description}
          </Typography>
        )}
      </Box>
      {typeof tab.badgeCount === "number" && tab.badgeCount > 0 && (
        <Badge
          badgeContent={tab.badgeCount}
          color={tab.badgeColor || "primary"}
          sx={{ ml: 1 }}
          {...tab.badgeProps}
        >
          <Box />
        </Badge>
      )}
      {tab.chipLabel && (
        <Chip
          label={tab.chipLabel}
          size='small'
          color={tab.chipColor || "default"}
          sx={{ ml: 1, height: 20, fontSize: "0.7rem" }}
          {...tab.chipProps}
        />
      )}
      {tab.labelAdornment}
    </Box>
  );

  if (resolvedTabs.length === 0) {
    return null;
  }

  const { sx: customTabsSx, ...restTabsProps } = tabsProps;

  return (
    <Box sx={{ width: "100%" }}>
      <Paper
        elevation={0}
        sx={{
          border: "1px solid",
          borderColor: "divider",
          mb: 3,
          overflow: "hidden",
        }}
        {...paperProps}
      >
        <Tabs
          value={activeValue}
          onChange={handleTabChange}
          aria-label='Section steps'
          sx={{
            minHeight: 60,
            "& .MuiTab-root": {
              minHeight: 60,
              fontWeight: "medium",
              fontSize: "0.9rem",
              textTransform: "none",
              px: 3,
              py: 2,
            },
            "& .Mui-selected": {
              color: "primary.main",
              fontWeight: "bold",
            },
            "& .MuiTabs-indicator": {
              height: 3,
            },
            bgcolor: (theme) => alpha(theme.palette.primary.main, 0.02),
            ...customTabsSx,
          }}
          {...restTabsProps}
        >
          {resolvedTabs.map((tab) => (
            <Tab
              key={tab.value}
              value={tab.value}
              label={renderLabel ? renderLabel(tab) : defaultRenderLabel(tab)}
              {...tab.tabProps}
            />
          ))}
        </Tabs>
      </Paper>

      <Box sx={{ mt: 2, ...contentWrapperSx }}>{activeTab?.content}</Box>
    </Box>
  );
};

export default SectionTabber;
